export class ApiError extends Error {
    status;
    code;
    constructor(status, code, message) {
        super(message);
        this.status = status;
        this.code = code;
        this.name = 'ApiError';
    }
    /** True when the server rejected a submitted game as unverifiable. */
    get isRejection() {
        return this.code === 'rejected';
    }
}
/**
 * Default ceiling on a single request.
 *
 * Deliberately generous: a cold Worker reached over a slow mobile link can
 * genuinely take this long, and giving up early on a connection that would
 * have answered is how an honest win gets thrown away. Nothing the player
 * looks at waits on a request — see `GameSession`, which reports the local
 * outcome the instant the board is finished — so a long ceiling costs
 * responsiveness nowhere.
 */
const DEFAULT_TIMEOUT_MS = 20_000;
/** Submitting carries the whole move log, so it gets more room than a read. */
const SUBMIT_TIMEOUT_MS = 30_000;
/**
 * True only when the runtime is *sure* there is no network. `navigator.onLine`
 * is famously optimistic — `true` proves nothing — but a `false` is reliable,
 * and acting on it turns a pointless 20-second timeout into an instant answer.
 */
function definitelyOffline() {
    const nav = globalThis.navigator;
    return nav !== undefined && nav.onLine === false;
}
export class MinesweeperClient {
    baseUrl;
    token;
    timeoutMs;
    constructor(options) {
        this.baseUrl = options.baseUrl.replace(/\/+$/, '');
        this.token = options.token ?? null;
        this.timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    }
    setToken(token) {
        this.token = token;
    }
    get hasToken() {
        return this.token !== null && this.token.length > 0;
    }
    async request(method, path, body, timeoutMs) {
        if (definitelyOffline()) {
            throw new ApiError(0, 'network', 'You are offline.');
        }
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), timeoutMs ?? this.timeoutMs);
        const headers = { Accept: 'application/json' };
        if (body !== undefined)
            headers['Content-Type'] = 'application/json';
        if (this.token)
            headers['Authorization'] = `Bearer ${this.token}`;
        let response;
        try {
            response = await fetch(`${this.baseUrl}${path}`, {
                method,
                headers,
                body: body === undefined ? undefined : JSON.stringify(body),
                signal: controller.signal,
            });
        }
        catch (error) {
            const reason = error instanceof Error && error.name === 'AbortError' ? 'timed out' : 'failed';
            throw new ApiError(0, 'network', `Request ${reason}. Check your connection.`);
        }
        finally {
            clearTimeout(timer);
        }
        const text = await response.text();
        const payload = text.length > 0 ? safeParse(text) : null;
        if (!response.ok) {
            const problem = payload;
            throw new ApiError(response.status, problem?.error ?? 'http_error', problem?.message ?? `Request failed with status ${response.status}.`);
        }
        return payload;
    }
    register(username) {
        return this.request('POST', '/v1/players', { username });
    }
    me() {
        return this.request('GET', '/v1/me');
    }
    rename(username) {
        return this.request('PATCH', '/v1/me', { username });
    }
    createGame(difficulty, custom) {
        return this.request('POST', '/v1/games', { difficulty, custom });
    }
    /** Commits the opening click and, in exchange, receives the board's seed. */
    startGame(gameId, x, y) {
        return this.request('POST', `/v1/games/${encodeURIComponent(gameId)}/start`, { x, y });
    }
    submitGame(gameId, moves) {
        return this.request('POST', `/v1/games/${encodeURIComponent(gameId)}/submit`, { moves }, SUBMIT_TIMEOUT_MS);
    }
    leaderboard(difficulty, window, options = {}) {
        const params = new URLSearchParams({ difficulty, window });
        if (options.sort)
            params.set('sort', options.sort);
        if (options.limit !== undefined)
            params.set('limit', String(options.limit));
        return this.request('GET', `/v1/leaderboard?${params.toString()}`);
    }
    /** All-time ranking by level/XP, independent of any one difficulty. */
    levelLeaderboard(options = {}) {
        const params = new URLSearchParams();
        if (options.limit !== undefined)
            params.set('limit', String(options.limit));
        const qs = params.toString();
        return this.request('GET', `/v1/leaderboard/levels${qs ? `?${qs}` : ''}`);
    }
}
function safeParse(text) {
    try {
        return JSON.parse(text);
    }
    catch {
        return null;
    }
}
//# sourceMappingURL=client.js.map